import React from 'react';
import { ReactSearchAutocomplete } from 'react-search-autocomplete';
import { PlaylistSong } from '../../types/PlaylistTypes';
import { useSongSearch } from './hooks/useSongSearch';

interface SongSearchProps {
    onSongSelect: (song: PlaylistSong) => void;
    selectedSongs: PlaylistSong[];
    placeholder?: string;
    disabled?: boolean;
    showAddButton?: boolean;
}

const SongSearchComponent: React.FC<SongSearchProps> = ({
    onSongSelect,
    selectedSongs,
    placeholder = "Search for songs to add",
    disabled = false,
    showAddButton = false
}) => {
    const { songs, isLoading, error } = useSongSearch();

    const handleOnSearch = (string: string, results: PlaylistSong[]) => {
        // This function can be used for any search-related logic if needed
    };

    const handleOnSelect = (item: PlaylistSong) => {
        if (disabled) return;
        onSongSelect(item);
    };

    const formatResult = (item: PlaylistSong) => {
        // Get item ID for comparison
        const itemId = item.melon_song_id || item.id;

        // Check if this song is already selected
        const isAlreadyAdded = selectedSongs.some(song => {
            const songId = song.melon_song_id || song.id;
            return itemId && songId && itemId === songId;
        });

        return (
            <div className="search-result-item">
                <div className="search-result-content">
                    <img
                        src={item.s3_picture_url || "/dubuFace.jpg"}
                        alt={item.name || "Song"}
                        className="search-result-image"
                        onError={(e) => {
                            e.currentTarget.src = "/dubuFace.jpg";
                            e.currentTarget.onerror = null;
                        }}
                    />
                    <div className="search-result-text">
                        <div className="search-result-name">{item.name || "Unknown Song"}</div>
                        <div className="search-result-artist">{item.artist_english_name || "Unknown Artist"}</div>
                    </div>
                </div>
                {showAddButton && (
                    <button
                        className="add-song-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOnSelect(item);
                        }}
                        disabled={isAlreadyAdded || disabled}
                    >
                        {isAlreadyAdded ? "Added" : "Add"}
                    </button>
                )}
            </div>
        );
    };

    if (error) {
        return <div className="search-error">Error loading songs: {error}</div>;
    }

    return (
        <div className="song-search-container">
            <ReactSearchAutocomplete
                items={songs}
                onSearch={handleOnSearch}
                onSelect={handleOnSelect}
                formatResult={formatResult}
                showItemsOnFocus={true}
                placeholder={placeholder}
                maxResults={showAddButton ? 15 : 10}
                autoFocus={false}
                showIcon={true}
                showClear={true}
                fuseOptions={{
                    keys: [
                        { name: "name", weight: 0.7 },
                        { name: "artist_english_name", weight: 0.3 },
                    ],
                    threshold: 0.3,
                    minMatchCharLength: 2,
                    includeScore: true
                }}
                styling={showAddButton ? {
                    height: "40px",
                    border: "1px solid #dfe1e5",
                    borderRadius: "24px",
                    backgroundColor: "white",
                    boxShadow: "rgba(32, 33, 36, 0.28) 0px 1px 6px 0px",
                    hoverBackgroundColor: "#eee",
                    fontSize: "16px",
                    iconColor: "grey",
                    lineColor: "rgb(232, 234, 237)",
                    placeholderColor: "grey",
                    clearIconMargin: "3px 14px 0 0",
                } : undefined}
            />
        </div>
    );
};

export default SongSearchComponent; 